import React, { useEffect, useState } from 'react';
import { FormControl } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import InputLabel from '@material-ui/core/InputLabel';
import ListItem from '@material-ui/core/ListItem';
import Select from '@material-ui/core/Select';
import { makeStyles, type Theme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { colors } from '@skyslope/mache';
import { v4 as uuid } from 'uuid';
import { validateInput } from '@repo/utils';
import { ReactComponent as NeedsToSignIcon } from '../../../images/need_to_sign.svg';
import { ReactComponent as ReceiveCopyIcon } from '../../../images/receive_copy.svg';
import { chameleonElId, checkIfRecipientIsSender } from '../../utils';
import { type IGroup, type IRecipient, RecipientRole } from '../types';
import RecipientTextField from './RecipientTextField';

const useStyles = makeStyles((theme: Theme) => ({
  recipient: {
    backgroundColor: 'white',
    padding: `${theme.spacing(2)}px ${theme.spacing(2.5)}px`,
    border: `1px solid ${colors.grey[400]}`,
    marginTop: theme.spacing(2),
  },
  label: {
    width: 'auto',
  },
  icon: {
    paddingRight: theme.spacing(3),
    height: 24,
  },
}));

type Props = {
  recipient: IRecipient | undefined;
  signingGroups: IGroup[];
  returnToRecipientList: () => void;
  saveRecipient: (recipient: IRecipient, isNew: boolean) => void;
  recipients: IRecipient[];
  lastGroupEmpty: boolean;
  contacts: any[];
  modal: boolean;
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
const RecipientAddOrEdit = (props: Props) => {
  const classes = useStyles();
  const { recipient, modal } = props;
  const [disabled, setDisabled] = useState(false);
  const [firstname, setFirstname] = useState('');
  const [lastname, setLastname] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<RecipientRole>(RecipientRole.Signer);
  const [signingGroupId, setSigningGroupId] = useState(props.signingGroups[0]?.id || '');
  const [error, setError] = useState({
    firstname: false,
    lastname: false,
    email: false,
    role: false,
  });

  let autofocus = 'firstname';
  if (recipient)
    autofocus = !recipient.firstName
      ? 'firstname'
      : !recipient.lastName
        ? 'lastname'
        : !recipient.email
          ? 'email'
          : 'firstname';

  const fieldsToRender = [
    {
      type: 'firstname',
      label: 'First Name',
      helperText: 'Please enter a first name.',

      handleOnChange: (str: string) => setFirstname(str),
      state: firstname,
    },
    {
      type: 'lastname',
      label: 'Last Name',
      helperText: 'Please enter a last name.',

      handleOnChange: (str: string) => setLastname(str),
      state: lastname,
    },
    {
      type: 'email',
      label: 'Email',
      helperText: 'Email must be a valid email.',

      handleOnChange: (str: string) => setEmail(str),
      state: email,
    },
  ];

  useEffect(() => {
    if (recipient) {
      setFirstname(recipient.firstName);
      setLastname(recipient.lastName);
      setEmail(recipient.email?.trim() || '');
      setRole(recipient.role);
      setSigningGroupId(recipient.group?.id || props.signingGroups[0]?.id || '');
      if (checkIfRecipientIsSender(recipient)) {
        setDisabled(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This code was written prior to enforcing lint rules
  }, [recipient]);

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const setStateValue = (e: any, callback: React.Dispatch<React.SetStateAction<any>>) => {
    callback(e.target.value);
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const validateFormInput = (e: any, str: string) => {
    return setError({ ...error, [str]: validateInput(e, str) });
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const handleSave = () => {
    let newRecipient: IRecipient;
    let group;
    if (role === RecipientRole.Signer) {
      if (signingGroupId === 'new') {
        group = {
          id: uuid(),
          name: 'Signing Group',
          order: props.signingGroups.length,
        };
      } else {
        group = props.signingGroups.find((sg) => sg.id === signingGroupId);
      }
    }
    if (!recipient) {
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      newRecipient = {
        email: email.trim(),
        firstName: firstname,
        lastName: lastname,
        color: '',
        role,
        id: uuid(),
        group,
        isPlaceholder: false,
      };
    } else {
      newRecipient = {
        ...recipient,
        firstName: firstname,
        lastName: lastname,
        email: email?.trim(),
        role,
        group,
      };
    }
    clearForm();
    props.saveRecipient(newRecipient, !recipient);
    props.returnToRecipientList();
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const cancelEditWrapper = () => {
    clearForm();
    props.returnToRecipientList();
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const clearForm = () => {
    setFirstname('');
    setLastname('');
    setEmail('');
    setRole(RecipientRole.Signer);
    setSigningGroupId('');
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const isValidInput = (str: string, attr: string) => {
    if (str.trim().length !== 0) {
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      error[attr] = false;
      return true;
    }
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    error[attr] = true;
    return false;
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const areThereAnyErrors = () => {
    if (
      email?.length <= 0 ||
      email === null ||
      firstname?.length <= 0 ||
      firstname === null ||
      lastname?.length <= 0 ||
      lastname === null
    ) {
      return true;
    }
    return Object.values(error).includes(true);
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const handleChange = (field: any, e: any) => {
    field.handleOnChange(e.target.value);
    validateFormInput(e.target.value, field.type);
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const handleContactSelect = (e: any, queryType: string) => {
    if (e) {
      setFirstname(e.firstName);
      setLastname(e.lastName);
      setEmail(e.email);
      validateFormInput(e.firstName, 'firstname');
      validateFormInput(e.lastName, 'lastname');
      validateFormInput(e.email, 'email');
    } else if (queryType === 'firstName') {
      setFirstname('');
    } else if (queryType === 'lastName') {
      setLastname('');
    } else {
      setEmail('');
    }
  };

  return (
    <div className="mt-8" data-spec="add-recipient-form">
      <Typography variant="overline">NEW RECIPIENT DETAILS</Typography>
      <div className="flex flex-col">
        {fieldsToRender.map((field) => (
          <RecipientTextField
            disabled={disabled}
            autoFocus={field.type === autofocus}
            label={field.label}
            // @ts-expect-error -- This code was written prior to enforcing lint rules
            helperText={error[field.type] && field.helperText}
            placeholder={field.label}
            handleOnChange={(e) => {
              handleChange(field, e);
            }}
            // @ts-expect-error -- This code was written prior to enforcing lint rules
            error={error[field.type]}
            value={field.state}
            dataSpec={`${field.type}-new`}
            contacts={props.contacts}
            handleContactSelect={handleContactSelect}
          />
        ))}
        <div className="mt-4 flex gap-2">
          <FormControl className="flex-1">
            <InputLabel className={classes.label} id={chameleonElId(12)}>
              Action Required
            </InputLabel>
            <Select
              classes={{ select: 'flex items-center' }}
              variant="filled"
              displayEmpty
              onChange={(e) => setStateValue(e, setRole)}
              value={role}
              required
              error={isValidInput(role, 'role')}
              disabled={recipient?.isPlaceholder && modal}
            >
              <ListItem value={RecipientRole.Signer}>
                <NeedsToSignIcon className={classes.icon} />
                Needs to sign
              </ListItem>
              <ListItem value={RecipientRole.CarbonCopy}>
                <ReceiveCopyIcon className={classes.icon} />
                Receives a copy
              </ListItem>
            </Select>
          </FormControl>
          <FormControl className="shrink-0">
            <InputLabel className={classes.label}>Signing Group</InputLabel>
            <Select
              variant="filled"
              displayEmpty
              onChange={(e) => setStateValue(e, setSigningGroupId)}
              value={role === RecipientRole.Signer ? signingGroupId : ''}
              disabled={role !== RecipientRole.Signer || !props.recipients.some((r) => r.role === RecipientRole.Signer)}
              required
            >
              {props.signingGroups.map((sg, i) => (
                <ListItem key={sg.id} value={sg.id}>
                  {i + 1}
                </ListItem>
              ))}
              {props.signingGroups.length < 4 &&
              !props.lastGroupEmpty &&
              props.recipients.length > props.signingGroups.length ? (
                <ListItem value="new">{props.signingGroups.length + 1}</ListItem>
              ) : null}
            </Select>
          </FormControl>
        </div>

        {recipient?.title ? (
          <div className="grid mt-4">
            <InputLabel className={classes.label}>Role</InputLabel>
            <Select disabled value={recipient.title}>
              <ListItem value={recipient.title}>{recipient.title}</ListItem>
            </Select>
          </div>
        ) : null}

        <div className="mt-6 w-full flex justify-end items-center gap-2">
          <Button data-spec="cancel" variant="text" color="primary" onClick={cancelEditWrapper} className="py-3 px-6">
            Cancel
          </Button>
          <Button
            variant="outlined"
            color="primary"
            data-spec="saveButton"
            disabled={areThereAnyErrors()}
            onClick={handleSave}
          >
            Save
          </Button>
        </div>
      </div>
    </div>
  );
};

// eslint-disable-next-line import/no-default-export -- This code was written prior to enforcing lint rules
export default RecipientAddOrEdit;
